import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";

export const RETENTION_BREAKDOWN_CHART_TOP_MAX = 50;
export const RETENTION_BREAKDOWN_CHART_TOP_DEFAULT = 6;

export function clampBreakdownChartTopN(raw) {
  const x = Math.round(Number(raw));
  if (!Number.isFinite(x)) return RETENTION_BREAKDOWN_CHART_TOP_DEFAULT;
  return Math.min(RETENTION_BREAKDOWN_CHART_TOP_MAX, Math.max(1, x));
}

function retentionBreakdownTotalUsers(bd) {
  const cohorts = Array.isArray(bd?.cohorts) ? bd.cohorts : [];
  return cohorts.reduce((sum, c) => sum + (Number(c?.cohortSize) || 0), 0);
}

export function sortBreakdownsByTotalUsersDesc(breakdowns) {
  if (!Array.isArray(breakdowns) || breakdowns.length === 0) return [];
  return [...breakdowns].sort((a, b) => {
    const db = retentionBreakdownTotalUsers(b);
    const da = retentionBreakdownTotalUsers(a);
    if (db !== da) return db - da;
    return String(a?.segment || "").localeCompare(String(b?.segment || ""), undefined, { sensitivity: "base" });
  });
}

/** Top segments by total profile count (sum of cohort sizes); used for chart series only. */
export function sliceBreakdownsForChart(breakdowns, topN) {
  if (!Array.isArray(breakdowns) || breakdowns.length === 0) return [];
  const cap = clampBreakdownChartTopN(topN);
  return sortBreakdownsByTotalUsersDesc(breakdowns).slice(0, cap);
}

/**
 * Mixpanel-style control under the breakdown column: limits how many breakdown lines appear in charts.
 */
export default function BreakdownTopValuesControl({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [triggerHover, setTriggerHover] = useState(false);
  const [draft, setDraft] = useState(() => String(clampBreakdownChartTopN(value)));
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const clamped = clampBreakdownChartTopN(value);
  const showMax = clamped >= RETENTION_BREAKDOWN_CHART_TOP_MAX;

  useEffect(() => {
    setDraft(String(clampBreakdownChartTopN(value)));
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const updatePos = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    };
    updatePos();
    const onDoc = (e) => {
      const t = e.target;
      if (menuRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const commitDraft = () => {
    const n = clampBreakdownChartTopN(draft);
    onChange(n);
    setDraft(String(n));
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
          setDraft(String(clamped));
        }}
        onMouseEnter={() => setTriggerHover(true)}
        onMouseLeave={() => setTriggerHover(false)}
        className="inline-flex max-w-full items-center gap-0.5 text-left"
        style={{
          margin: 0,
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.01em",
          color: triggerHover ? theme.text || "#334155" : theme.textMuted || "var(--text-muted)",
          textTransform: "none",
          lineHeight: 1.35,
        }}
        title="Lines shown in chart (table lists all segments)"
      >
        <span>
          Top {clamped} · Total Profile(s)
        </span>
        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.75 }}>
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="dialog"
            aria-label="Top values"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: 260,
              padding: "12px 14px",
              backgroundColor: theme.bgRaised || "var(--surface-base)",
              border: `1px solid ${theme.border}`,
              borderRadius: 10,
              boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
              zIndex: 10000,
              fontFamily: "Inter, sans-serif",
              textTransform: "none",
            }}
          >
            <div className="text-[13px] font-semibold" style={{ color: theme.text }}>
              Top values
            </div>
            <p className="mt-1 text-[11px] leading-snug" style={{ color: theme.textMuted }}>
              Choose the number of lines to show in the chart, up to {RETENTION_BREAKDOWN_CHART_TOP_MAX}. The table
              always lists all segments.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={RETENTION_BREAKDOWN_CHART_TOP_MAX}
                disabled={showMax}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => {
                  if (!showMax) {
                    commitDraft();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!showMax) commitDraft();
                  }
                }}
                className="w-14 rounded border px-1.5 py-1 text-[13px]"
                style={{ borderColor: theme.border, color: theme.text }}
              />
              <span className="text-[12px]" style={{ color: theme.textMuted }}>
                lines
              </span>
            </div>
            <label className="mt-3 flex cursor-pointer items-start gap-2 text-[12px]" style={{ color: theme.text }}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={showMax}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange(RETENTION_BREAKDOWN_CHART_TOP_MAX);
                    setDraft(String(RETENTION_BREAKDOWN_CHART_TOP_MAX));
                  } else {
                    onChange(RETENTION_BREAKDOWN_CHART_TOP_DEFAULT);
                    setDraft(String(RETENTION_BREAKDOWN_CHART_TOP_DEFAULT));
                  }
                }}
              />
              <span>Show max lines (up to {RETENTION_BREAKDOWN_CHART_TOP_MAX})</span>
            </label>
          </div>,
          document.body
        )}
    </>
  );
}
